import { zodResolver } from '@hookform/resolvers/zod';
import { CircleLoader } from 'components/CircleLoader';
import { ApplyContext, ApplyStep } from 'context/apply.context';
import React, {
  KeyboardEventHandler,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState
} from 'react';
import { useDropzone } from 'react-dropzone';
import { useForm } from 'react-hook-form';
import { axiosInstance } from 'services/axios';

import { Input, SvgUse } from '@dnse/ui';

import {
  BACKSPACE_KEY_CODE,
  PHONE_MAX_LENGTH,
  SubmitApplicationInput,
  submitApplicationSchema
} from './submit.validation';

const MB_TO_B = 1024 ** 2;
interface Props {
  jobTitle?: string;
  jobId?: string;
  file?: File;
  callback?: () => void;
  toggleModal?: (value: boolean) => void;
}

const handleInputPhone: KeyboardEventHandler<HTMLInputElement> = event => {
  // Only ASCII character in that range allowed
  const ASCIICode = event.which || event.keyCode;
  // @ts-expect-error Ignore type checking
  // prettier-ignore
  // eslint-disable-next-line unicorn/prefer-code-point, no-restricted-globals, unicorn/prefer-number-properties
  if (ASCIICode !== BACKSPACE_KEY_CODE && isNaN(String.fromCharCode(ASCIICode))) {
    event.preventDefault();
  }
  if (event.currentTarget.value.length >= PHONE_MAX_LENGTH) {
    event.preventDefault();
  }
};

export const ApplicationForm: React.FC<Props> = ({
  jobTitle,
  jobId,
  file,
  callback,
  toggleModal
}) => {
  const { resume, setStep } = useContext(ApplyContext);
  const [isLoading, setIsLoading] = useState(false);
  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    setError,
    watch,
    getValues
  } = useForm({
    mode: 'onSubmit',
    reValidateMode: 'onSubmit',
    resolver: zodResolver(submitApplicationSchema),
    defaultValues: {
      applicantFullname: '',
      applicantEmail: '',
      applicantPhone: '',
      resume,
      requestPosition: ''
    }
  });

  const onDrop = useCallback(
    droppedFiles => {
      setValue('resume', droppedFiles[0], { shouldValidate: false });
    },
    [setValue]
  );
  const {
    acceptedFiles = resume,
    getRootProps,
    getInputProps
  } = useDropzone({
    noKeyboard: true,
    maxFiles: 1,
    accept: {
      'application/pdf': [],
      'application/msword': [],
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document':
        []
    },
    onDrop
  });

  useEffect(() => {
    if (file) {
      console.log(file);
      setValue('resume', file, { shouldValidate: false });
    }
  }, [file]);

  const { resume: _uploadedFile } = watch();

  const handleSubmitForm = async (data: SubmitApplicationInput) => {
    setIsLoading(true);
    const formData = new FormData();
    formData.append('resume', data.resume);
    formData.append('fullname', data.applicantFullname);
    formData.append('email', data.applicantEmail);
    formData.append('phone', data.applicantPhone);
    formData.append('request_position', data.requestPosition);
    formData.append('job_title', jobTitle || 'Ứng viên tự do');
    if (jobId) {
      formData.append('applied_job', jobId);
    }
    try {
      await axiosInstance.post('api/upload-cv', formData);
      setStep(ApplyStep.SubmitSuccessfully);
    } catch (error) {
      console.log(error);
    } finally {
      setIsLoading(false);
      if (callback) {
        callback();
      }
    }
  };

  const changeFileRef = useRef(null);

  const getFileFromFileList = (file: File | FileList) => {
    return file instanceof FileList && file.length > 0 ? file[0] : file;
  };
  return (
    <form
      data-testid="apply-form"
      id="application-form"
      className={`flex-col flex gap-y-6 w-full lg:max-w-[526px] ${
        jobTitle && `mb-[50px]`
      } px-4 lg:px-0`}
      onSubmit={handleSubmit(handleSubmitForm)}
    >
      <div>
        {jobTitle ? (
          <>
            <h4 className="text-base font-semibold mb-2 text-brand-300">
              Vị trí ứng tuyển
            </h4>
            <h4 className="text-base font-semibold mb-4">{jobTitle}</h4>
            <h4 className="text-base font-semibold text-brand-300 mb-2">
              Thông tin liên hệ
            </h4>
          </>
        ) : (
          <div className="gap-y-2">
            <div className="text-2xl font-medium mb-2">Thông tin liên hệ</div>
            <div className="text-sm font-normal">
              Hãy để lại thông tin cá nhân để DNSE có thể liên hệ với bạn nhé!
              🥰
            </div>
            <SvgUse
              id="close-outline-2"
              className="absolute top-[24px] right-[24px] w-[24px] h-[24px] cursor-pointer"
              onClick={() => toggleModal(false)}
            />
          </div>
        )}
        <Input
          id="fullname"
          label={'Họ và tên'}
          inputProps={{
            ...register('applicantFullname'),
            autoComplete: 'new-fullname',
            autoFocus: true,
            'aria-autocomplete': 'none',
            list: 'autocompleteOff',
            onChange: () => setError('applicantFullname', { message: '' })
          }}
          errMessage={errors?.applicantFullname?.message}
        />
      </div>
      <Input
        id="email"
        label={'Email'}
        inputProps={{
          ...register('applicantEmail'),
          autoComplete: 'new-email',
          'aria-autocomplete': 'none',
          list: 'autocompleteOff',
          onChange: () => setError('applicantEmail', { message: '' })
        }}
        errMessage={errors?.applicantEmail?.message}
      />
      <Input
        id="phone"
        label={'Số điện thoại'}
        inputProps={{
          ...register('applicantPhone'),
          onKeyPress: handleInputPhone,
          autoComplete: 'new-phone',
          list: 'autocompleteOff',
          'aria-autocomplete': 'none',
          onChange: () => setError('applicantPhone', { message: '' })
        }}
        errMessage={errors?.applicantPhone?.message}
      />
      {!jobTitle && (
        <Input
          id="position"
          label={'Vị trí mong muốn'}
          inputProps={{
            ...register('requestPosition'),
            autoComplete: 'new-request-position',
            list: 'autocompleteOff',
            'aria-autocomplete': 'none'
          }}
        />
      )}
      <div>
        <div
          data-testid="input-resume"
          style={{
            border: '1px dashed var(--light-textlgiht, #9DA0A6)',
            borderRadius: '10px'
          }}
          className="flex flex-col items-center gap-y-2 px-[40px] py-[13px]"
        >
          {_uploadedFile ? (
            <div {...getRootProps()} className="flex flex-col items-center">
              <span className="text-base font-medium">
                {getFileFromFileList(_uploadedFile)?.name}
              </span>
              <span className="text-xs font-normal text-gray-800">
                {(getFileFromFileList(_uploadedFile)?.size / MB_TO_B).toFixed(
                  2
                )}
                MB
              </span>
              <button
                type={'button'}
                className="text-center text-base font-medium text-brand-300 underline underline-offset-2 px-3 py-[9px]"
              >
                Thay đổi hồ sơ
              </button>

              <input
                ref={changeFileRef}
                type="file"
                {...getInputProps()}
                onChange={e => {
                  setValue(
                    'resume',
                    getFileFromFileList(e.currentTarget.files),
                    {
                      shouldValidate: true
                    }
                  );
                }}
              />
            </div>
          ) : (
            <div {...getRootProps()} className="flex flex-col items-center">
              <input
                type="file"
                {...getInputProps()}
                onChange={e => {
                  setValue(
                    'resume',
                    getFileFromFileList(e.currentTarget.files),
                    {
                      shouldValidate: true
                    }
                  );
                }}
              />
              <SvgUse id={'upload-outline-2'} width={32} height={32} />
              <span className="text-base font-medium">
                Kéo hồ sơ của bạn vào đây
              </span>
              <span className="text-xs font-normal text-gray-800">
                Hoặc bạn có thể
              </span>
              <button
                type={'button'}
                className="text-center text-base font-medium text-brand-300 underline underline-offset-2 px-3 py-[9px]"
              >
                Chọn từ máy tính
              </button>
              <span className="text-xs font-normal text-gray-800">
                Hỗ trợ định dạng .doc, .docx, pdf có kích thước tối đa 3MB
              </span>
            </div>
          )}
        </div>
        <span
          className="text-xs text-red-500 font-normal"
          data-testid="error-resume"
        >
          {errors?.resume?.message}
        </span>
      </div>
      <button
        data-testid="submit-button"
        type="submit"
        form="application-form"
        className="px-6 py-[14px] rounded-[40px] text-center w-full text-base font-medium text-white bg-brand-300 col-span-3 flex items-center justify-center gap-x-2"
        disabled={isLoading}
      >
        {isLoading ? <CircleLoader color="white" /> : null} Gửi hồ sơ
      </button>
    </form>
  );
};
